// libraryDuplicates.js — V2-AC-26 (#87): the duplicate warning, transport-free.
//
// When a carton being saved to the library, or shared with the organisation,
// has the same die AND the same artwork as one the user can see, the backend
// (#86, contract 1.14) answers 409 `duplicate_carton` with the matches in
// `detail.duplicates`. It never blocks: the same request with
// `allow_duplicate=true` saves anyway, and the server records who did it.
//
// The bench's background save sends DRAFTS, and the backend never checks a
// draft — so the bench can never meet this warning. benchUploadArgs below keeps
// the client from ever asking for an override on that path either.
//
// Everything here is pure, so src/libraryDuplicates.test.js runs the real code
// through the generated mirror (node tools/gen-cjs-mirrors.cjs).

export const DUPLICATE_CODE = 'duplicate_carton';

/** V2-AC-26's own words — shown when the server sends none. */
export const DUPLICATE_MESSAGE = 'Duplicate carton detected: A carton with the '
  + 'same specifications already exists in the library.';

/**
 * The query string for POST /library/cartons. Purely additive: with neither
 * `draft` nor `allowDuplicate`, it is byte-identical to what was sent before
 * either existed.
 */
export function uploadQuery({ name, draft = false, allowDuplicate = false } = {}) {
  const qs = new URLSearchParams();
  if (name) qs.set('name', name);
  if (draft) qs.set('draft', 'true');
  if (allowDuplicate) qs.set('allow_duplicate', 'true');
  return qs.toString() ? `?${qs}` : '';
}

/** The body for PATCH /library/cartons/{id}: only the fields being changed. */
export function patchBody({ name, visibility, origin, allowDuplicate = false } = {}) {
  const patch = {};
  if (name != null) patch.name = name;
  if (visibility != null) patch.visibility = visibility;
  // PR ⑧ §4: promote a draft to the curated library. One-way — the backend
  // refuses (bad_origin) anything other than 'library'.
  if (origin != null) patch.origin = origin;
  if (allowDuplicate) patch.allow_duplicate = true;
  return patch;
}

/**
 * What the bench's background save uploads: a draft named after the file, and
 * never an override, whatever else the bench item carries.
 */
export function benchUploadArgs(carton, draftUploads) {
  return {
    file: carton.file,
    name: String(carton.name || '').replace(/\.pdf$/i, ''),
    draft: !!draftUploads,
  };
}

/** True only for the duplicate warning — not for any other refusal. */
export function isDuplicateRefusal(err) {
  return !!err && err.status === 409 && err.code === DUPLICATE_CODE;
}

/** The cartons the warning links to, as the server listed them. */
export function readDuplicates(err) {
  const list = err && err.body && err.body.detail && err.body.detail.duplicates;
  if (!Array.isArray(list)) return [];
  return list.filter((d) => d && d.id).map((d) => ({
    id: String(d.id),
    name: d.name || 'Untitled carton',
    ownerName: d.owner_name || null,
    shared: d.visibility === 'org',
    createdAt: d.created_at || null,
  }));
}

/**
 * Everything the dialog shows for one refused save or share. `action` is
 * 'save' (upload, Save to library) or 'share'; `subject` names the carton or
 * file being saved.
 */
export function duplicateDialogModel(err, { action = 'save', subject = null } = {}) {
  const detail = (err && err.body && err.body.detail) || {};
  return {
    title: 'Duplicate carton detected',
    message: detail.message || DUPLICATE_MESSAGE,
    subject,
    items: readDuplicates(err),
    confirmLabel: action === 'share' ? 'Share anyway' : 'Save anyway',
    cancelLabel: 'Cancel',
  };
}

/**
 * A library card's "Duplicate" badge: the cartons it duplicates that are in
 * the list on screen (the server only names ones this user can see).
 */
export function duplicatePartners(carton, cartonsById) {
  const ids = carton && Array.isArray(carton.duplicate_of) ? carton.duplicate_of : [];
  return ids
    .filter((id) => cartonsById && cartonsById[id])
    .map((id) => ({ id, name: cartonsById[id].name }));
}
